/**
 * Home Route
 * Main invoice generator page
 */

import { createFileRoute } from '@tanstack/react-router';
import { MainLayout } from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { StructuredData, generateFAQSchema, generateHowToSchema, generateBreadcrumbSchema } from '@/components/seo/StructuredData';

export const Route = createFileRoute('/')({
  component: () => {
    // FAQ Schema - Enhanced with more comprehensive questions
    const faqData = generateFAQSchema([
      {
        question: "Is the Smart Invoice Generator really free?",
        answer: "Yes! Our invoice generator is completely free to use with no hidden fees, subscriptions, or credit card requirements. Generate unlimited invoices forever."
      },
      {
        question: "Do I need to create an account?",
        answer: "No account required. You can generate and download invoices instantly without signing up, verifying your email, or providing any personal information."
      },
      {
        question: "What formats can I export my invoices in?",
        answer: "You can export your invoices in both PDF and PNG formats. PDF is perfect for professional printing and email attachments, while PNG works great for messaging apps and quick sharing."
      },
      {
        question: "Can I customize my invoice template?",
        answer: "Absolutely! Upload your logo, choose from 150+ currencies, customize colors, add payment terms, include tax calculations, and personalize every aspect of your invoice."
      },
      {
        question: "How does the AI-powered generation work?",
        answer: "Our AI intelligently fills invoice details, calculates totals automatically, applies discounts and taxes, formats dates and numbers correctly, and ensures professional layout every time."
      },
      {
        question: "Is my invoice data stored on your servers?",
        answer: "No! All invoice generation happens locally in your browser. We don't store, transmit, or have access to your invoice data on our servers. Your information stays private on your device."
      },
      {
        question: "How many invoices can I create?",
        answer: "Unlimited! Create as many invoices as you need. There are no quotas, rate limits, or restrictions. Our service is designed to handle freelancers and businesses of all sizes."
      },
      {
        question: "Does it work on mobile devices?",
        answer: "Yes! Smart Invoice Generator is fully responsive and optimized for mobile phones, tablets, and desktop computers. Create professional invoices from anywhere on any device."
      },
      {
        question: "Can I save and manage multiple invoices?",
        answer: "Yes! We provide an invoice history feature that saves your recent invoices locally. You can access, view, and re-download previously created invoices anytime."
      },
      {
        question: "What should I include in my invoice?",
        answer: "Include your business name and contact info, client details, invoice number, date, due date, description of products/services with quantities and prices, subtotal, taxes, discounts, and total amount due."
      }
    ]);

    // HowTo Schema
    const howToData = generateHowToSchema({
      name: "How to Create a Professional Invoice",
      description: "Learn how to create a professional invoice in seconds using Smart Invoice Generator",
      steps: [
        {
          name: "Enter Business Details",
          text: "Fill in your business name, address, and contact information"
        },
        {
          name: "Add Client Information",
          text: "Enter your client's details and billing information"
        },
        {
          name: "Add Line Items",
          text: "Add products or services with descriptions, quantities, and prices"
        },
        {
          name: "Customize & Preview",
          text: "Review your invoice, add a logo, and customize the appearance"
        },
        {
          name: "Export & Share",
          text: "Download your invoice as PDF or PNG and send it to your client"
        }
      ]
    });

    // Breadcrumb Schema
    const breadcrumbData = generateBreadcrumbSchema([
      { name: "Home", url: "https://invoicegenerated.com/" }
    ]);


    return (
      <>
        <SEOHead
          title="Smart Invoice Generator - Free AI-Powered Invoice Creator | Create Professional Invoices"
          description="Create professional, customizable invoices in seconds with our free AI-powered invoice generator. Support for 150+ currencies, instant PDF export, intelligent form filling. Unlimited invoices, no sign-up required."
          keywords="invoice generator, free invoice maker, AI invoice creator, professional invoice template, PDF invoice, online invoice generator, billing software, invoice creator, free invoice tool, freelance invoice, small business invoicing"
          canonical="https://invoicegenerated.com/"
        />
        <StructuredData data={faqData} />
        <StructuredData data={howToData} />
        <StructuredData data={breadcrumbData} />

        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
          <MainLayout />
        </div>
      </>
    );
  },
});
